import { useState } from 'react';
import { extractErrorMessage } from '../../api/errors';
import { useGlobalPaywall, useSetGlobalPaywall } from '../../queries/globalPaywall';
import { useToast } from '../../contexts/ToastContext';
import { Card, CardContent } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { cn } from '../../lib/utils';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

export function GlobalPaywallToggle() {
  const { data, isLoading } = useGlobalPaywall();
  const setPaywall = useSetGlobalPaywall();
  const { success, error } = useToast();
  const [confirming, setConfirming] = useState(false);

  if (isLoading || !data) {
    return <Spinner />;
  }

  const apply = (enabled: boolean) =>
    setPaywall.mutate(enabled, {
      onSuccess: () => success(enabled ? 'Paywall enabled site-wide.' : 'Paywall disabled site-wide.'),
      onError: (err) => error(extractErrorMessage(err, 'Failed to update the paywall.')),
    });

  const handleToggleClick = () => {
    // Disabling the paywall site-wide is the dangerous direction — confirm before
    // that one; re-enabling has no destructive downside.
    if (data.enabled) {
      setConfirming(true);
      return;
    }
    apply(true);
  };

  return (
    <Card className="max-w-lg">
      <CardContent className="py-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold">Global Paywall</h3>
            <p className="text-sm text-muted-foreground">
              When disabled, subscription checks are bypassed site-wide — an emergency kill-switch, not a normal setting.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={data.enabled}
            onClick={handleToggleClick}
            disabled={setPaywall.isPending}
            className={cn(
              'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
              data.enabled ? 'bg-primary' : 'bg-destructive',
            )}
          >
            <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform', data.enabled ? 'translate-x-6' : 'translate-x-1')} />
          </button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          {data.enabled ? 'Enabled' : 'Disabled'} · Last updated {DATE_FORMATTER.format(new Date(data.lastUpdated))} · {data.environment}
        </p>
      </CardContent>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Disable the paywall site-wide?"
        description="This bypasses subscription checks for every customer across the whole site until you re-enable it. Use only as an emergency kill-switch."
        confirmLabel="Disable paywall"
        destructive
        busy={setPaywall.isPending}
        onConfirm={() => {
          setConfirming(false);
          apply(false);
        }}
      />
    </Card>
  );
}
